/**
 * reCAPTCHA v3 for the e-mail verification step. The API scores the token before it will say
 * whether an address is the right one (SharePass-Infra `pull-secret/funnel.js`, "Captcha
 * required."), so without one the reveal page can never get past that question.
 *
 * Loaded on demand rather than on every reveal: only secrets protected by e-mail need it, and the
 * script watches the page it is loaded on.
 */
const DEFAULT_SITE_KEY = '6Le_zDIeAAAAAM7B4Mqb4sPyREULQULJPaBffKPq';

/**
 * The site key this build uses, or null for none. A SharePass One build sets `off`: its API never
 * scores the token (`funnel.js` checks it only for SharePass and Pro), and our key is not
 * registered for a client's domain, so loading it only shows "Invalid domain for site key" to
 * the recipient.
 */
export const resolveSiteKey = (raw: string | undefined): string | null => {
    const value = (raw ?? '').trim();
    if (value.toLowerCase() === 'off') return null;
    return value || DEFAULT_SITE_KEY;
};

const SITE_KEY = resolveSiteKey(import.meta.env.VITE_RECAPTCHA_KEY);

/** How long to wait for a token. A key refused for the domain can leave `execute` pending forever. */
const TOKEN_TIMEOUT_MS = 8000;

const SCRIPT_ID = 'sharepass-recaptcha';

interface Grecaptcha {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
    interface Window {
        grecaptcha?: Grecaptcha;
    }
}

let loading: Promise<Grecaptcha | null> | null = null;

const loadScript = (siteKey: string): Promise<Grecaptcha | null> => {
    if (window.grecaptcha) return Promise.resolve(window.grecaptcha);

    loading ??= new Promise<Grecaptcha | null>((resolve) => {
        const existing = document.getElementById(SCRIPT_ID);
        const script = existing instanceof HTMLScriptElement ? existing : document.createElement('script');

        script.id = SCRIPT_ID;
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.onload = () => resolve(window.grecaptcha ?? null);
        script.onerror = () => resolve(null);

        if (!existing) document.head.appendChild(script);
    });

    return loading;
};

/**
 * A token for `action`, or null when reCAPTCHA is off for this build or cannot run (offline,
 * blocked by an extension, a key refused for the domain). The caller sends the request anyway:
 * the API decides, and a null token simply fails its check.
 */
export const getCaptchaToken = async (action: string, siteKey: string | null = SITE_KEY): Promise<string | null> => {
    if (!siteKey) return null;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS);
    });
    const token = (async (): Promise<string | null> => {
        const grecaptcha = await loadScript(siteKey);
        if (!grecaptcha) return null;

        await new Promise<void>((resolve) => grecaptcha.ready(resolve));
        return await grecaptcha.execute(siteKey, { action });
    })();

    try {
        return await Promise.race([token, timeout]);
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
};
